import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowUp,
  faArrowDown,
  faExclamationCircle,
  faCheckSquare,
  faGift,
  faPercent,
} from '@fortawesome/free-solid-svg-icons';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchSalesData,
  fetchItemSalesData,
  fetchMonthlySalesData,
  SalesData,
} from '../../../redux/SalesDetailed';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import DateSelector from '../../../commonComponents/DateSelector';
import ChartSelection from '../../../commonComponents/ChartSelection';
import BarLineChart from '../../../commonComponents/BarLineChart';
import CustomersDropDown from '../../Loyalty/CustomersDropdown';
import StoresDropDown from '../StoresDropDown';
import { useTheme } from '../../../context/ThemeContext';
import {
  Container,
  HeaderSection,
  BackButton,
  HeaderContent,
  TitleRow,
  Title,
  Subtitle,
  FiltersRow,
  FilterLabel,
  ChartCard,
  ChartControls,
  ChartMetrics,
  MetricLabel,
  MetricIconSmall,
  MetricText,
  ChartContainer,
} from './styles';
import { DateRange, TransformedSalesData, Merchant, Store, User, SalesRankingItem } from './types';
import SalesSummary from './SalesSummary';
import SalesRankingTable from './SalesRankingTable';

const SalesDetailedView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { monthlyChange, loading, itemSalesLoading } = useSelector(
    (state: RootState) => state.salesDetailed
  );

  const [salesRankingData, setSalesRankingData] = useState<SalesRankingItem[]>([]);
  const [salesAllData, setSalesAllData] = useState<SalesData | null>(null);
  const [transformedSalesData, setTransformedSalesData] = useState<TransformedSalesData[] | null>(null);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [storeId, setStoreId] = useState<string>('');
  const [chartType, setChartType] = useState<string>('line');
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment().subtract(7, 'days'),
    endDate: moment(),
  });
  const [selectedFilter] = useState<string>('');
  const user: User = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (user?.role === 'merchant' && !merchant) {
      setMerchant(user as Merchant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params1: any = {
          filter: selectedFilter,
          merchantId: merchant?.id,
        };
        const params2: any = {
          startDate: moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
          endDate: moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
          merchantId: merchant?.id,
        };

        if (storeId) {
          params1.store = storeId;
          params2.store = storeId;
        }

        const params = isFilter ? params1 : params2;

        const [salesResult, itemSalesResult] = await Promise.all([
          dispatch(fetchSalesData(params)).unwrap(),
          dispatch(fetchItemSalesData(params)).unwrap(),
        ]);

        if (salesResult) {
          const transformedData = salesResult.salesData.map((item) => ({
            date: item.period,
            sales: (parseFloat(String(item.amount)) || 0).toFixed(2),
            YLabelsales: item.amount,
            orderCount: item.count || 0,
            averageOrderValue: parseFloat(String(item.averageOrderValue || 0)).toFixed(2),
          }));
          setTransformedSalesData(transformedData);
          setSalesAllData(salesResult);
        }

        if (itemSalesResult) {
          const sortedItems = [...itemSalesResult.items].sort(
            (a, b) => b.totalTimesSold - a.totalTimesSold
          );
          const rankingData = sortedItems.map((item, index) => ({
            id: index + 1,
            name: item.item,
            unitsSold: item.totalTimesSold,
          }));
          setSalesRankingData(rankingData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, selectedDateRange.startDate, selectedDateRange.endDate, merchant?.id, storeId, isFilter]);

  useEffect(() => {
    if (storeId) {
      dispatch(fetchMonthlySalesData({ storeId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const handleSelection = (selection: DateRange) => {
    setSelectedDateRange(selection);
  };

  const addCommasInPrices = (value: number | string): string => {
    return (parseFloat(String(value)) || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <Container>
        <HeaderSection>
          <BackButton onClick={() => navigate(-1)} aria-label="Go Back to Analytics Dashboard">
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
          <HeaderContent>
            <TitleRow>
              <Title>Sales</Title>
              <FiltersRow>
                {user.role === 'admin' && (
                  <>
                    <FilterLabel>By Merchants</FilterLabel>
                    <CustomersDropDown
                      onOptionSelect={(merch: Merchant) => {
                        setMerchant(merch);
                      }}
                    />
                  </>
                )}
                <FilterLabel>By Store</FilterLabel>
                <StoresDropDown
                  onOptionSelect={(store: Store) => {
                    setStoreId(store.id);
                  }}
                  merchantId={merchant?.id}
                  role={user?.role}
                />
              </FiltersRow>
            </TitleRow>
            <Subtitle>A summary of your revenue and sales by channel.</Subtitle>
          </HeaderContent>
        </HeaderSection>

        <SalesSummary monthlyChange={monthlyChange} merchant={merchant} storeId={storeId} />

        <ChartCard>
          <ChartControls>
            <DateRangePicker
              selectedDateRange={selectedDateRange}
              onDateRangeChange={(range: DateRange) => {
                setIsFilter(false);
                setSelectedDateRange(range);
              }}
            />
            <DateSelector onDateSelect={handleSelection} />
            <ChartSelection onChange={(type: string) => setChartType(type)} />
          </ChartControls>
          {(loading || itemSalesLoading) ? (
            <div style={{ padding: '20px', textAlign: 'center', color: isDarkMode ? '#a0aec0' : '#718096' }}>
              Loading chart data...
            </div>
          ) : (
            <>
              <ChartMetrics>
                <MetricLabel>
                  <MetricIconSmall icon={faArrowUp} color="#48bb78" />
                  <span>
                    Net :{' '}
                    <MetricText>
                      $
                      {addCommasInPrices(
                        (parseFloat(String(salesAllData?.grossSales)) || 0) -
                          ((parseFloat(user?.tax || '0')) + (parseFloat(String(salesAllData?.tips)) || 0))
                      )}
                    </MetricText>
                  </span>
                </MetricLabel>
                <MetricLabel>
                  <MetricIconSmall icon={faArrowDown} color="#f56565" />
                  <span>
                    Gross : <MetricText>${addCommasInPrices(parseFloat(String(salesAllData?.grossSales)) || 0)}</MetricText>
                  </span>
                </MetricLabel>
                <MetricLabel>
                  <MetricIconSmall icon={faExclamationCircle} color="#9f7aea" />
                  <span>
                    Refunds : <MetricText>${addCommasInPrices(parseFloat(String(salesAllData?.refunds)) || 0)}</MetricText>
                  </span>
                </MetricLabel>
                <MetricLabel>
                  <MetricIconSmall icon={faCheckSquare} color="#48bb78" />
                  <span>
                    Order Count : <MetricText>{parseInt(String(salesAllData?.orderCount)) || 0}</MetricText>
                  </span>
                </MetricLabel>
                <MetricLabel>
                  <MetricIconSmall icon={faGift} color="#9f7aea" />
                  <span>
                    Tips : <MetricText>${addCommasInPrices(parseFloat(String(salesAllData?.tips)) || 0)}</MetricText>
                  </span>
                </MetricLabel>
                <MetricLabel>
                  <MetricIconSmall icon={faPercent} color="#4299e1" />
                  <span>
                    Total Tax :{' '}
                    <MetricText>
                      $
                      {addCommasInPrices(
                        (parseFloat(String(salesAllData?.netSales)) || 0) * (parseFloat(user?.tax || '0'))
                      )}
                    </MetricText>
                  </span>
                </MetricLabel>
              </ChartMetrics>
              <ChartContainer>
                <BarLineChart
                  data={transformedSalesData}
                  chartType={chartType}
                  xKey="date"
                  yKey="YLabelsales"
                  valueKey="sales"
                  isLoading={false}
                />
              </ChartContainer>
            </>
          )}
        </ChartCard>

        <SalesRankingTable data={salesRankingData} />
      </Container>
    </ThemeProvider>
  );
};

export default SalesDetailedView;
